import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

/**
 * Helper function to extract YYYY-MM-DD date string
 * Backend now sends dates in YYYY-MM-DD format consistently
 */
const extractDateString = (dateInput) => {
  if (!dateInput) return null;
  
  // Backend sends dates in YYYY-MM-DD format
  if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateInput;
  }
  
  // Fallback for any other format
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function SchoolAnalytics({ attendanceData, student, dateRange }) {
  // Calculate school-level daily attendance statistics
  const calculateSchoolAnalytics = () => {
    console.log('SchoolAnalytics - attendanceData:', attendanceData);
    console.log('SchoolAnalytics - student:', student);
    console.log('SchoolAnalytics - dateRange:', dateRange);
    
    if (!attendanceData?.records) {
      console.log('No records found in attendanceData');
      return null;
    }

    // Group records by date
    const recordsByDate = {};
    
    attendanceData.records.forEach(record => {
      const date = extractDateString(record.date);
      if (!date) return; // Skip invalid dates
      
      if (!recordsByDate[date]) {
        recordsByDate[date] = [];
      }
      recordsByDate[date].push(record);
    });

    console.log('Records by date:', recordsByDate);

    let daysPresent = 0;
    let daysLate = 0;
    let daysAbsent = 0;

    // Get all unique dates that actually have records (actual school days)
    const actualSchoolDays = Object.keys(recordsByDate);
    
    // Also get any days from the date range that might be missing records
    // but could still be valid school days (for absent calculation)
    let startDate, endDate;
    
    if (attendanceData.dateRange && attendanceData.dateRange.startDate) {
      const startDateStr = extractDateString(attendanceData.dateRange.startDate);
      const endDateStr = extractDateString(attendanceData.dateRange.endDate);
      const [sY, sM, sD] = startDateStr.split('-').map(Number);
      const [eY, eM, eD] = endDateStr.split('-').map(Number);
      startDate = new Date(sY, sM - 1, sD);
      endDate = new Date(eY, eM - 1, eD);
    } else {
      // Fallback: get range from actual records
      const recordDateStrs = attendanceData.records.map(r => extractDateString(r.date)).filter(Boolean);
      if (recordDateStrs.length > 0) {
        recordDateStrs.sort();
        const [sY, sM, sD] = recordDateStrs[0].split('-').map(Number);
        const [eY, eM, eD] = recordDateStrs[recordDateStrs.length - 1].split('-').map(Number);
        startDate = new Date(sY, sM - 1, sD);
        endDate = new Date(eY, eM - 1, eD);
      } else {
        startDate = new Date();
        endDate = new Date();
      }
    }
    
    // Get all possible weekdays in the range
    const allPossibleDays = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        allPossibleDays.push(`${year}-${month}-${day}`);
      }
    }
    
    // Only count days that either have records OR are within a reasonable school period
    // For now, let's use days that have records as our baseline for "valid school days"
    const validSchoolDays = actualSchoolDays;

    console.log('Valid school days (days with records):', validSchoolDays);
    console.log('All possible weekdays in range:', allPossibleDays);

    // Analyze each day according to the logic
    validSchoolDays.forEach(date => {
      const dayRecords = recordsByDate[date] || [];
      
      if (dayRecords.length === 0) {
        // No records for the whole day = absent
        daysAbsent++;
      } else {
        // Sort records by time to find the first record of the day
        const sortedRecords = dayRecords.sort((a, b) => {
          const timeA = a.period?.startTime || '00:00';
          const timeB = b.period?.startTime || '00:00';
          return timeA.localeCompare(timeB);
        });
        
        const firstRecord = sortedRecords[0];
        console.log(`Date ${date}: Found ${dayRecords.length} records, First record status:`, firstRecord.status);
        
        if (firstRecord.status.toUpperCase() === 'PRESENT') {
          // First record is present = day present
          daysPresent++;
        } else if (firstRecord.status.toUpperCase() === 'LATE') {
          // First record is late = day late
          daysLate++;
        } else {
          // Check if there are any present or late records later in the day
          const hasLaterAttendance = sortedRecords.slice(1).some(record => 
            record.status.toUpperCase() === 'PRESENT' || record.status.toUpperCase() === 'LATE'
          );
          
          if (hasLaterAttendance) {
            // Has present/late record later in the day = day late
            daysLate++;
          } else {
            // No present/late records at all = day absent
            daysAbsent++;
          }
        }
      }
    });

    const result = {
      daysPresent,
      daysLate,
      daysAbsent,
      totalDays: validSchoolDays.length,
      attendanceRate: validSchoolDays.length > 0 ? (((daysPresent + daysLate) / validSchoolDays.length) * 100) : 0
    };

    console.log('School analytics result:', result);
    return result;
  };

  // Prepare chart data for daily attendance visualization
  const prepareChartData = () => {
    if (!attendanceData?.records) return null;

    // Group records by date and calculate daily attendance percentage
    const recordsByDate = {};
    
    attendanceData.records.forEach(record => {
      const date = extractDateString(record.date);
      if (!date) return; // Skip invalid dates
      
      if (!recordsByDate[date]) {
        recordsByDate[date] = [];
      }
      recordsByDate[date].push(record);
    });

    // Sort dates and prepare chart data
    const sortedDates = Object.keys(recordsByDate).sort();
    const labels = sortedDates.map(date => {
      // Parse YYYY-MM-DD string safely to avoid timezone shifts
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-');
        const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        // Fallback for other formats
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    });

    const attendancePercentages = sortedDates.map(date => {
      const dayRecords = recordsByDate[date];
      const totalRecords = dayRecords.length;
      const presentRecords = dayRecords.filter(r => 
        r.status.toUpperCase() === 'PRESENT' || r.status.toUpperCase() === 'LATE'
      ).length;
      
      if (totalRecords > 0) {
        const percentage = (presentRecords / totalRecords) * 100;
        // If student is absent for all periods, show a small red bar (5%)
        if (percentage === 0) {
          return 5;
        }
        return percentage;
      }
      return 0;
    });

    // Create color coding based on the same logic as school analytics
    const backgroundColors = sortedDates.map(date => {
      const dayRecords = recordsByDate[date];
      if (dayRecords.length === 0) return '#ef4444'; // Absent - red
      
      const sortedRecords = dayRecords.sort((a, b) => {
        const timeA = a.period?.startTime || '00:00';
        const timeB = b.period?.startTime || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      const firstRecord = sortedRecords[0];
      if (firstRecord.status.toUpperCase() === 'PRESENT') {
        return '#22c55e'; // Present - green
      } else if (firstRecord.status.toUpperCase() === 'LATE') {
        return '#f59e0b'; // Late - yellow
      } else {
        // Check if there are any present or late records later in the day
        const hasLaterAttendance = sortedRecords.slice(1).some(record => 
          record.status.toUpperCase() === 'PRESENT' || record.status.toUpperCase() === 'LATE'
        );
        
        if (hasLaterAttendance) {
          return '#f59e0b'; // Has attendance later = Late - yellow
        } else {
          return '#ef4444'; // No attendance at all = Absent - red
        }
      }
    });

    // Store detailed records for tooltip access
    const dailyRecordsData = sortedDates.map(date => recordsByDate[date]);

    return {
      labels,
      datasets: [
        {
          label: 'Daily Attendance %',
          data: attendancePercentages,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors.map(color => color),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }
      ],
      dailyRecords: dailyRecordsData // Store for tooltip access
    };
  };

  const chartData = prepareChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Daily School Attendance Overview',
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#1e293b'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const dayRecords = chartData.dailyRecords[dataIndex];
            const date = chartData.labels[dataIndex];
            
            if (!dayRecords || dayRecords.length === 0) {
              return `${date}: 0% - No records found`;
            }

            // Calculate the real percentage
            const totalRecords = dayRecords.length;
            const presentCount = dayRecords.filter(r => r.status.toUpperCase() === 'PRESENT').length;
            const lateCount = dayRecords.filter(r => r.status.toUpperCase() === 'LATE').length;
            const absentCount = dayRecords.filter(r => r.status.toUpperCase() === 'ABSENT').length;
            
            const realPercentage = totalRecords > 0 ? ((presentCount + lateCount) / totalRecords) * 100 : 0;
            
            return [
              `${date}: ${realPercentage.toFixed(1)}% attendance`,
              `Total periods: ${totalRecords}`,
              `Present: ${presentCount}, Late: ${lateCount}, Absent: ${absentCount}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Attendance Percentage'
        }
      },
      x: {
        title: {
          display: true,
          text: 'School Days'
        }
      }
    }
  };

  const schoolAnalytics = calculateSchoolAnalytics();

  if (!attendanceData || !student || !schoolAnalytics) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🏫</span>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">School Analytics</h3>
          <p className="text-slate-600 mb-6">
            Select a student and ensure attendance data is loaded to view school-level analytics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <p className="text-slate-600">
          Daily attendance overview for <span className="font-semibold text-purple-600">{student.first_name} {student.last_name}</span>
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Based on first daily record and presence throughout the day
        </p>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Days Present Card */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">
                {schoolAnalytics.daysPresent}
              </div>
              <div className="text-sm text-green-500 font-medium">
                / {schoolAnalytics.totalDays}
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-green-800 mb-1">Days Present</h3>
            <p className="text-xs text-green-600">
              First record of day marked present
            </p>
            <div className="mt-2 text-xs text-green-700">
              {schoolAnalytics.totalDays > 0 ? 
                `${((schoolAnalytics.daysPresent / schoolAnalytics.totalDays) * 100).toFixed(1)}% of total days` : 
                'No data available'
              }
            </div>
          </div>
        </div>

        {/* Days Late Card */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-yellow-600">
                {schoolAnalytics.daysLate}
              </div>
              <div className="text-sm text-yellow-500 font-medium">
                / {schoolAnalytics.totalDays}
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-yellow-800 mb-1">Days Late</h3>
            <p className="text-xs text-yellow-600">
              First record late or later attendance
            </p>
            <div className="mt-2 text-xs text-yellow-700">
              {schoolAnalytics.totalDays > 0 ? 
                `${((schoolAnalytics.daysLate / schoolAnalytics.totalDays) * 100).toFixed(1)}% of total days` : 
                'No data available'
              }
            </div>
          </div>
        </div>

        {/* Days Absent Card */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-red-600">
                {schoolAnalytics.daysAbsent}
              </div>
              <div className="text-sm text-red-500 font-medium">
                / {schoolAnalytics.totalDays}
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-800 mb-1">Days Absent</h3>
            <p className="text-xs text-red-600">
              No attendance records for the day
            </p>
            <div className="mt-2 text-xs text-red-700">
              {schoolAnalytics.totalDays > 0 ? 
                `${((schoolAnalytics.daysAbsent / schoolAnalytics.totalDays) * 100).toFixed(1)}% of total days` : 
                'No data available'
              }
            </div>
          </div>
        </div>

        {/* Overall Attendance Rate Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {schoolAnalytics.attendanceRate.toFixed(1)}%
              </div>
              <div className="text-sm text-blue-500 font-medium">
                attendance rate
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Daily Attendance</h3>
            <p className="text-xs text-blue-600">
              Days with attendance / total school days
            </p>
            <div className="mt-2">
              <div className="w-full bg-blue-100 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${Math.min(100, schoolAnalytics.attendanceRate)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Attendance Chart */}
      {chartData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Daily Attendance Pattern</h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                <span className="text-slate-600">Present</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
                <span className="text-slate-600">Late</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                <span className="text-slate-600">Absent</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <Bar data={chartData} options={chartOptions} />
          </div>
          <div className="mt-4 text-sm text-slate-600">
            <p>Each bar shows what percentage of classes the student attended on that day.</p>
            <p>Bar color indicates the status of the first class: Green (Present), Yellow (Late), Red (Absent).</p>
          </div>
        </div>
      )}

      {/* Methodology Explanation */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
          <svg className="w-5 h-5 text-slate-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Daily Attendance Logic
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-2">
              <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
              <span className="font-semibold text-green-800">Day Present</span>
            </div>
            <p className="text-slate-600">
              Student's first record of the day is marked as "Present"
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center mb-2">
              <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
              <span className="font-semibold text-yellow-800">Day Late</span>
            </div>
            <p className="text-slate-600">
              First record is "Late" OR has present/late records later in the day
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-red-200">
            <div className="flex items-center mb-2">
              <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
              <span className="font-semibold text-red-800">Day Absent</span>
            </div>
            <p className="text-slate-600">
              No attendance records found for the entire day
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
